/**
 * Typed provider gateway config (IoT push) API facade over the generated contract client.
 */

import { z } from 'zod';
import { providerGatewayConfigApi } from '../api/generated/providerGatewayConfig.api.js';
import { EfemGatewayConfigVersionConfigResponseSchema } from '@cnapse/shared/efem-gateway-config-schemas.js';
import {
  GatewayConfigPushHistoryResponseSchema,
  GatewayConfigResendMqttResponseSchema,
} from '@cnapse/shared/gateway-config-push-schemas.js';
import {
  type EfemGatewayConfigPreviewResponse,
  type EfemGatewayConfigPushResponse,
  type EfemGatewayOverridesResponse,
  type EfemGatewayRegistryTemplateResponse,
} from '@cnapse/shared/schemas.js';

class ProviderGatewayConfigService {
  async getGatewayConfigPreview(serialNumber: string): Promise<EfemGatewayConfigPreviewResponse> {
    return providerGatewayConfigApi.getConfigPreview({ serialNumber }) as Promise<EfemGatewayConfigPreviewResponse>;
  }

  async getGatewayConfigRegistryTemplate(
    serialNumber: string,
  ): Promise<EfemGatewayRegistryTemplateResponse> {
    return providerGatewayConfigApi.getRegistryTemplate({ serialNumber }) as Promise<EfemGatewayRegistryTemplateResponse>;
  }

  async postGatewayConfigPreview(
    serialNumber: string,
    body?: { overrides?: Record<string, unknown> },
  ): Promise<EfemGatewayConfigPreviewResponse> {
    return providerGatewayConfigApi.postConfigPreview({ serialNumber }, body ?? {}) as Promise<EfemGatewayConfigPreviewResponse>;
  }

  async pushGatewayConfig(
    serialNumber: string,
    body: {
      siteId?: string;
      overrides?: Record<string, unknown>;
      floorId?: string;
      forwardingHost?: string;
      forwardingPort?: number;
      pushTimeOverrides?: Record<string, unknown>;
    },
  ): Promise<{ success: boolean; data: unknown }> {
    return providerGatewayConfigApi.pushConfig({ serialNumber }, body) as Promise<{ success: boolean; data: unknown }>;
  }

  async pushEfemGatewayConfig(
    serialNumber: string,
    body: { overrides?: Record<string, unknown>; siteId?: string },
  ): Promise<EfemGatewayConfigPushResponse> {
    return providerGatewayConfigApi.pushConfig({ serialNumber }, body) as Promise<EfemGatewayConfigPushResponse>;
  }

  async getGatewayConfigPushHistory(
    serialNumber: string,
  ): Promise<z.infer<typeof GatewayConfigPushHistoryResponseSchema>> {
    return providerGatewayConfigApi.getPushHistory({ serialNumber }) as Promise<
      z.infer<typeof GatewayConfigPushHistoryResponseSchema>
    >;
  }

  async getGatewayConfigHistoryVersion(
    serialNumber: string,
    version: string,
  ): Promise<z.infer<typeof EfemGatewayConfigVersionConfigResponseSchema>> {
    return providerGatewayConfigApi.getPushVersionConfig({ serialNumber, version }) as Promise<
      z.infer<typeof EfemGatewayConfigVersionConfigResponseSchema>
    >;
  }

  async resendGatewayConfigMqtt(
    serialNumber: string,
    version: string,
  ): Promise<z.infer<typeof GatewayConfigResendMqttResponseSchema>> {
    return providerGatewayConfigApi.resendMqtt({ serialNumber, version }, {}) as Promise<
      z.infer<typeof GatewayConfigResendMqttResponseSchema>
    >;
  }

  async getGatewayConfigOverrides(serialNumber: string): Promise<EfemGatewayOverridesResponse> {
    return providerGatewayConfigApi.getGatewayOverrides({ serialNumber }) as Promise<EfemGatewayOverridesResponse>;
  }

  async saveGatewayConfigOverrides(
    serialNumber: string,
    overrides: Record<string, unknown>,
  ): Promise<EfemGatewayConfigPreviewResponse> {
    return providerGatewayConfigApi.putGatewayOverrides({ serialNumber }, { overrides }) as Promise<EfemGatewayConfigPreviewResponse>;
  }

  async getTenantConfigOverrides(tenantId: string): Promise<EfemGatewayOverridesResponse> {
    return providerGatewayConfigApi.getTenantGatewayOverrides(
      { tenantId },
      { gatewayType: 'efem' },
    ) as Promise<EfemGatewayOverridesResponse>;
  }

  async saveTenantConfigOverrides(
    tenantId: string,
    overrides: Record<string, unknown>,
  ): Promise<EfemGatewayOverridesResponse> {
    return providerGatewayConfigApi.putTenantGatewayOverrides(
      { tenantId },
      { gatewayType: 'efem' },
      { overrides },
    ) as Promise<EfemGatewayOverridesResponse>;
  }
}

export const providerGatewayConfigService = new ProviderGatewayConfigService();
